import { v4 as generateUuid } from "uuid";

import {
    poraTestMechanismUrl,
    serverUrl,
    STRAPI_QUESTIONS_GROUPS_URL,
    zingApiAuthKey,
} from "@/constants/endpoints";
import { STRAPI_API_KEY } from "@/constants/env";
import {
    AuthTokenResponse,
    PurchaseProductData,
    ShareVideoResponse,
    TestVariantResponse,
    UserUuidResponse,
} from "@/types/api";
import { StrapiResponse } from "@/types/strapi";

const RENDER_WORKOUT_VIDEO_API = `${serverUrl}api/v3/video/workout-video-share`;
const GENERATED_WORKOUT_UUID_PARAM = "generated_workout_uuid";
const GET_USER_UUID_BY_EMAIL_API = `${serverUrl}api/v3/user/get-user-uuid-by-email/`;
const AUTH_TOKEN_API = `${serverUrl}api/v1/user/auth/`;
const USER_PROFILE_API = `${serverUrl}api/v1/user/profile/`;
const CREATE_ACCOUNT_API = `${serverUrl}api/v3/user/auth0/create-database-connection-account/`;
const HANDLE_PURCHASE_API = `${serverUrl}api/v3/subscriptions/handle-purchase/`;
const MARKETING_ATTRACTION_API = `${serverUrl}api/v3/marketing/attraction-info/`;

// Pora AI test combination mechanism
const TEST_MECHANISM_KEY = "key";
const GET_TEST_VARIANT = `${poraTestMechanismUrl}sample`;
const VIEW_TEST_VARIANT = `${poraTestMechanismUrl}add_view`;
const ADD_CONVRSION_TO_TEST_VARIANT = `${poraTestMechanismUrl}add_click`;

export const getGeneratedVideo = async (generatedWorkoutUuid: string) => {
    try {
        const targetURL = new URL(RENDER_WORKOUT_VIDEO_API);
        targetURL.searchParams.append(GENERATED_WORKOUT_UUID_PARAM, generatedWorkoutUuid);

        const response = await fetch(targetURL.toString(), {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "X-Request-ID": generateUuid(),
            },
        });

        const data = (await response.json()) as ShareVideoResponse;
        return data;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getUserUuidApi = async (body: unknown): Promise<UserUuidResponse | null> => {
    try {
        const response = await fetch(GET_USER_UUID_BY_EMAIL_API, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Api-Key ${zingApiAuthKey}`,
                "X-Request-ID": generateUuid(),
            },
            body: JSON.stringify(body),
        });

        const data = (await response.json()) as UserUuidResponse;

        if (response.status === 400) {
            return data;
        }

        return data;
    } catch (err) {
        console.error(err);
        return null;
    }
};
export const getAuthTokenApi = async (uuid: string): Promise<AuthTokenResponse | null> => {
    try {
        const res = await fetch(AUTH_TOKEN_API, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Request-ID": generateUuid(),
            },
            body: JSON.stringify({ uuid }),
        });
        return (await res.json()) as AuthTokenResponse;
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const updateProfileApi = async (authToken: string, body: object, uuid: string) => {
    try {
        await fetch(USER_PROFILE_API, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${authToken}`,
                "X-Request-ID": generateUuid(),
            },
            body: JSON.stringify({ ...body, uuid }),
        });
    } catch (err) {
        console.error(err);
    }
};

export const getProfileApi = async (auth_token: string): Promise<unknown | null> => {
    try {
        const profile = await fetch(USER_PROFILE_API, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${auth_token}`,
                "X-Request-ID": generateUuid(),
            },
        });

        return await profile.json();
    } catch (err) {
        console.error(err);
        return null;
    }
};

export const createAccountApi = async (userUUID: string, password: string) => {
    try {
        const request = {
            user_uuid: userUUID,
            auth0_password: password,
        };
        const response = await fetch(CREATE_ACCOUNT_API, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Api-Key ${zingApiAuthKey}`,
                "X-Request-ID": generateUuid(),
            },
            body: JSON.stringify(request),
        });
        return response;
    } catch (err) {
        console.error(err);
    }
};

export const purchaseProductApi = async (
    { uuid, ident, featureName }: PurchaseProductData,
    onComplete: () => void,
    onError: () => void
) => {
    try {
        const response = await fetch(HANDLE_PURCHASE_API, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Api-Key ${zingApiAuthKey}`,
                "X-Request-ID": generateUuid(),
            },
            body: JSON.stringify({
                user_uuid: uuid,
                ident: ident,
                feature_name: featureName,
            }),
        });

        const data = await response.json();

        if (data.status === "ok") {
            onComplete();
        } else {
            onError();
        }
    } catch (e) {
        onError();
    }
};

export const getTestMechanismVariant = async (variantsKeys: string[]) => {
    const targetURL = new URL(GET_TEST_VARIANT);
    variantsKeys.forEach((variantKey) =>
        targetURL.searchParams.append(TEST_MECHANISM_KEY, variantKey)
    );
    const response = await fetch(targetURL.toString(), {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "X-Request-ID": generateUuid(),
        },
    });
    const data = (await response.json()) as TestVariantResponse;
    return data.sampled_key;
};

export const setTestMechanismVariantSeen = async (variantKey: string) => {
    try {
        const targetURL = new URL(VIEW_TEST_VARIANT);
        targetURL.searchParams.append(TEST_MECHANISM_KEY, variantKey);
        await fetch(targetURL.toString(), {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "X-Request-ID": generateUuid(),
            },
        });
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const setTestMechanismVariantConversionEvent = async (variantKey: string) => {
    try {
        const targetURL = new URL(ADD_CONVRSION_TO_TEST_VARIANT);
        targetURL.searchParams.append(TEST_MECHANISM_KEY, variantKey);
        await fetch(targetURL.toString(), {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "X-Request-ID": generateUuid(),
            },
        });
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const sendMarketingAttractionInfo = async (
    uuid: string,
    data: any,
    orderId: string | null | undefined
) => {
    try {
        const authData = await getAuthTokenApi(uuid);
        const request = {
            ttclid: data.ttClickId,
            ttp: data.ttCookie,
            user_agent: data.userAgent,
            ip_address: data.ipAddress,
            epik: data.epik,
            fbp: data.fbp,
            fbc: data.fbc,
            gclid: data.gclid,
            country: data.country,
            city: data.city,
            zip_code: data.postalCode,
            state: data.state,
            order_id: orderId,
        };

        const response = await fetch(MARKETING_ATTRACTION_API, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Token ${authData?.auth_token}`,
                "X-Request-ID": generateUuid(),
            },
            body: JSON.stringify(request),
        });
        return response;
    } catch (err) {
        console.error(err);
    }
};

export const getStrapiQuestionsGroups = async () => {
    try {
        const response = await fetch(STRAPI_QUESTIONS_GROUPS_URL, {
            method: "GET",
            headers: {
                Authorization: `bearer ${STRAPI_API_KEY}`,
            },
        });

        const responseObj = (await response.json()) as StrapiResponse;
        return responseObj?.data;
    } catch (err) {
        console.error(err);
    }
};
